import React, {
    useCallback, useMemo, useState, useContext, useEffect
} from 'react';
import _ from 'lodash';
import htmlParser from 'html-react-parser';
import { CepConstants } from 'saga-cep-specific-components';
import CommonConstants from 'sg-common-constants';
import { SgScrollUtils, useSgTrackPageName } from 'saga-modules';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import {
    Accordion, AccordionCard, Button, Link
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import SgSptConstants from 'sg-spt-constants';
import SgCepSideMenu from 'sg-cep-side-menu';
import { BreakpointTrackerContext } from '@jutro/layout';
import metadata from './ClaimsPage.metadata.json5';
import styles from './ClaimsPage.module.scss';
import messages from './ClaimsPage.messages';

function ClaimsPage() {
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const [motorReadMoreOpened, setMotorReadMoreOpened] = useState(false);
    const [homeReadMoreOpened, setHomeReadMoreOpened] = useState(false);
    const [healthReadMoreOpened, setHealthReadMoreOpened] = useState(false);
    const [travelReadMoreOpened, setTravelReadMoreOpened] = useState(false);
    const claimsAssistanceNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_MOTOR_CLAIMS_ASSISTANCE_PHONE_NUMBER;
    const claimsGlassNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_MOTOR_CLAIMS_GLASS_PHONE_NUMBER;
    const claimsStandardNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_MOTOR_CLAIMS_STANDARD_PHONE_NUMBER;
    const claimsPmiNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_SMI_CLAIMS_NUMBER;
    const pmiNumber1 = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_HOW_TO_CLAIM_PMI_NUMBER1;

    useSgTrackPageName(CepConstants.PAGE_NAMES.HOW_TO_CLAIM);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const motorAccordions = useMemo(() => {
        return [
            {
                id: 'liability',
                header: translator(messages.LiabilityHeader),
                content: (<p>{translator(messages.LiabilityContent)}</p>)
            },
            {
                id: 'repairs',
                header: translator(messages.RepairsHeader),
                content: (<p>{translator(messages.RepairsContent)}</p>)
            },
            {
                id: 'hireCar',
                header: translator(messages.HireCarHeader),
                content: (<p>{translator(messages.HireCarContent)}</p>)
            },
            {
                id: 'totalLoss',
                header: translator(messages.TotalLossHeader),
                content: (<p>{translator(messages.TotalLossContent)}</p>)
            },
            {
                id: 'fireTheft',
                header: translator(messages.FireTheftHeader),
                content: (<p>{translator(messages.FireTheftContent)}</p>)
            },
            {
                id: 'personalBelongings',
                header: translator(messages.PersonalBelongingsHeader),
                content: (<p>{translator(messages.PersonalBelongingsContent)}</p>)
            },
            {
                id: 'personalInjury',
                header: translator(messages.PersonalInjuryHeader),
                content: (<p>{translator(messages.PersonalInjuryContent)}</p>)
            },
            {
                id: 'motorExcess',
                header: translator(messages.MotorExcessHeader),
                content: (<p>{translator(messages.MotorExcessContent)}</p>)
            }
        ];
    }, [translator]);

    const homeAccordions = useMemo(() => {
        return {
            general: [
                {
                    id: 'suppliers',
                    header: translator(messages.SuppliersHeader),
                    content: (
                        <>
                            <p>{translator(messages.SuppliersContent1)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.SuppliersContent2)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.SuppliersContent3)}
                            </p>
                        </>
                    )
                },
                {
                    id: 'homeExcess',
                    header: translator(messages.HomeExcessHeader),
                    content: (
                        <>
                            <p>{translator(messages.HomeExcessContent1)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HomeExcessContent2)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HomeExcessContent3)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HomeExcessContent4)}
                            </p>
                        </>
                    )
                },
                {
                    id: 'insurerPartners',
                    header: translator(messages.InsurerPartnersHeader),
                    content: (
                        <>
                            <p>{translator(messages.InsurerPartnersContent1)}</p>
                            <p>{translator(messages.InsurerPartnersContent2)}</p>
                        </>
                    )
                },
                {
                    id: 'buildingOrContents',
                    header: translator(messages.BuildingOrContentsHeader),
                    content: (
                        <>
                            <p>{translator(messages.BuildingOrContentsContent1)}</p>
                            <p>{translator(messages.BuildingOrContentsContent2)}</p>
                        </>
                    )
                },
                {
                    id: 'beforeYouCall',
                    header: translator(messages.BeforeYouCallHeader),
                    content: (
                        <>
                            <p>{translator(messages.BeforeYouCallContent1)}</p>
                            <p>{translator(messages.BeforeYouCallContent2)}</p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.BeforeYouCallContent3)}
                            </p>
                            {translator(messages.Or)}
                            <p className="sgFaqTextCheck">
                                {translator(messages.BeforeYouCallContent4)}
                            </p>
                            {translator(messages.Or)}
                            <p className="sgFaqTextCheck">
                                {translator(messages.BeforeYouCallContent5)}
                            </p>
                            <p>{translator(messages.BeforeYouCallContent6)}</p>
                        </>
                    )
                },
                {
                    id: 'whatYouAsk',
                    header: translator(messages.WhatYouAskHeader),
                    content: (
                        <>
                            <p>{translator(messages.WhatYouAskContent1)}</p>
                            <p>{translator(messages.WhatYouAskContent2)}</p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatYouAskContent3)}
                            </p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatYouAskContent4)}
                            </p>
                            <p>{translator(messages.WhatYouAskContent5)}</p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatYouAskContent6)}
                            </p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatYouAskContent7)}
                            </p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatYouAskContent8)}
                            </p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatYouAskContent9)}
                            </p>
                        </>
                    )
                },
                {
                    id: 'whatNotCovered',
                    header: translator(messages.WhatNotCoveredHeader),
                    content: (
                        <>
                            <p>{translator(messages.WhatNotCoveredContent1)}</p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatNotCoveredContent2)}
                            </p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatNotCoveredContent3)}
                            </p>
                            <p className="sgFaqTextCheck">
                                {translator(messages.WhatNotCoveredContent4)}
                            </p>
                            <p>{translator(messages.WhatNotCoveredContent5)}</p>
                        </>
                    )
                }
            ],
            insideHome: [
                {
                    id: 'loft',
                    header: translator(messages.LoftHeader),
                    content: (
                        <>
                            <p>{translator(messages.LoftContent1)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.LoftContent2)}
                            </p>
                            <p>{translator(messages.LoftContent3)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.LoftContent4)}
                            </p>
                            <p>{translator(messages.LoftContent5)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.LoftContent6)}
                            </p>
                            <p>{translator(messages.LoftContent7)}</p>
                        </>
                    )
                },
                {
                    id: 'mainAreas',
                    header: translator(messages.MainAreasHeader),
                    content: (
                        <>
                            <p className="sgFaqTextBullet">
                                {translator(messages.MainAreasContent1)}
                            </p>
                            <p>{translator(messages.MainAreasContent2)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.MainAreasContent3)}
                            </p>
                            <p>{translator(messages.MainAreasContent4)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.MainAreasContent5)}
                            </p>
                            <p>{translator(messages.MainAreasContent6)}</p>
                        </>
                    )
                }
            ],
            outsideHome: [
                {
                    id: 'roof',
                    header: translator(messages.RoofHeader),
                    content: (
                        <>
                            <p className="sgFaqTextBullet">
                                {translator(messages.RoofContent1)}
                            </p>
                            <p>{translator(messages.RoofContent2)}</p>
                            <p>{translator(messages.RoofContent3)}</p>
                            <p>{translator(messages.RoofContent4)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.RoofContent5)}
                            </p>
                            <p>{translator(messages.RoofContent6)}</p>
                            <p>{translator(messages.RoofContent7)}</p>
                            <p>{translator(messages.RoofContent8)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.RoofContent9)}
                            </p>
                            <p>{translator(messages.RoofContent10)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.RoofContent11)}
                            </p>
                            <p>{translator(messages.RoofContent12)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.RoofContent13)}
                            </p>
                            <p>{translator(messages.RoofContent14)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.RoofContent15)}
                            </p>
                            <p>{translator(messages.RoofContent16)}</p>
                            <p>{translator(messages.RoofContent17)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.RoofContent18)}
                            </p>
                            <p>{translator(messages.RoofContent19)}</p>
                            <p>{translator(messages.RoofContent20)}</p>
                        </>
                    )
                },
                {
                    id: 'leadFlashings',
                    header: translator(messages.LeadFlashingsHeader),
                    content: (
                        <>
                            <p>{translator(messages.LeadFlashingsContent1)}</p>
                            <p>{translator(messages.LeadFlashingsContent2)}</p>
                        </>
                    )
                },
                {
                    id: 'chimneys',
                    header: translator(messages.ChimneysHeader),
                    content: (<p>{translator(messages.ChimneysContent)}</p>)
                },
                {
                    id: 'windowsDoors',
                    header: translator(messages.WindowsDoorsHeader),
                    content: (<p>{translator(messages.WindowsDoorsContent)}</p>)
                },
                {
                    id: 'outsideWalls',
                    header: translator(messages.OutsideWallsHeader),
                    content: (<p>{translator(messages.OutsideWallsContent)}</p>)
                }
            ]
        };
    }, [translator]);

    const healthAccordions = useMemo(() => {
        const PolicyBookLink = (
            <a rel="noreferrer" target="_blank" href={CepConstants.HEALTH_POLICY_BOOK_LINK}>
                {translator(messages.HealthPolicyBook)}
            </a>
        );

        return {
            general: [
                {
                    id: 'MakingClaim',
                    header: translator(messages.HealthMakingAClaim),
                    content: (
                        <>
                            <h3>{translator(messages.HealthStep1Header)}</h3>
                            <h4>{translator(messages.HealthClaimStep1HeaderP1)}</h4>
                            <p>
                                {translator(messages.HealthHealthClaimStep1P1)}
                                <Link
                                    href={CepConstants.CLAIM_PMI_BUPA_LINK_REFER}
                                    target="_blank"
                                >
                                    {translator(messages.BupaWebsite)}
                                    .
                                </Link>
                            </p>
                            <p>{translator(messages.HealthHealthClaimStep1P3)}</p>
                            <h3>{translator(messages.HealthStep2Header)}</h3>
                            <p>
                                {translator(messages.HealthHealthClaimStep2P1, {
                                    phoneNumber: isMobile
                                        ? <a href={`tel:${claimsPmiNumber.replaceAll(' ', '')}`}>{claimsPmiNumber}</a>
                                        : <b>{claimsPmiNumber}</b>
                                })}
                                <Link
                                    href={CepConstants.CLAIM_PMI_BUPA_LINK_REFER}
                                    target="_blank"
                                >
                                    {translator(messages.BupaWebsite)}
                                </Link>
                                {translator(messages.HealthHealthClaimStep2P1_2)}
                                <Link
                                    href={CepConstants.CLAIM_PMI_BUPA_LINK_APP}
                                    target="_blank"
                                >
                                    {translator(messages.HealthHealthClaimStep2P1_3)}
                                </Link>
                                .
                            </p>
                            <p>{translator(messages.HealthHealthClaimStep2P2)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthHealthClaimStep2P3)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthHealthClaimStep2P4)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthHealthClaimStep2P5)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthHealthClaimStep2P6)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthHealthClaimStep2P7)}
                            </p>
                            <h3>{translator(messages.HealthStep3Header)}</h3>
                            <p>{translator(messages.HealthHealthClaimStep3P1)}</p>
                            <p>{translator(messages.HealthHealthClaimStep3P2)}</p>
                            <p>{translator(messages.HealthHealthClaimStep3P3)}</p>
                            <h3>{translator(messages.HealthStep4Header)}</h3>
                            <p>{translator(messages.HealthHealthClaimStep4P1)}</p>
                            <p>{translator(messages.HealthHealthClaimStep4P2)}</p>
                        </>
                    )
                },
                {
                    id: 'GuidedCare',
                    header: translator(messages.HealthGuidedCareClaimsHeader),
                    content: (
                        <>
                            <h3>{translator(messages.HealthStep1Header)}</h3>
                            <h4>{translator(messages.HealthGuidedCareClaimsHeaderP1)}</h4>
                            <p>
                                {translator(messages.HealthGuidedCareClaimsStep1P1)}
                                <Link
                                    href={CepConstants.CLAIM_PMI_BUPA_LINK_REFER}
                                    target="_blank"
                                >
                                    {translator(messages.BupaWebsite)}
                                    .
                                </Link>
                            </p>
                            <p>{translator(messages.HealthGuidedCareClaimsStep1P2)}</p>
                            <h3>{translator(messages.HealthStep2Header)}</h3>
                            <p>
                                {translator(messages.HealthHealthClaimStep2P1, {
                                    phoneNumber: isMobile
                                        ? <a href={`tel:${claimsPmiNumber.replaceAll(' ', '')}`}>{claimsPmiNumber}</a>
                                        : <b>{claimsPmiNumber}</b>
                                })}
                                <Link
                                    href={CepConstants.CLAIM_PMI_BUPA_LINK_REFER}
                                    target="_blank"
                                >
                                    {translator(messages.BupaWebsite)}
                                </Link>
                                {translator(messages.HealthHealthClaimStep2P1_2)}
                                <Link
                                    href={CepConstants.CLAIM_PMI_BUPA_LINK_APP}
                                    target="_blank"
                                >
                                    {translator(messages.HealthHealthClaimStep2P1_3)}
                                </Link>
                                .
                            </p>
                            <p>{translator(messages.HealthGuidedCareClaimsStep2P2)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthGuidedCareClaimsStep2P3)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthGuidedCareClaimsStep2P4)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthGuidedCareClaimsStep2P5)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthGuidedCareClaimsStep2P6)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.HealthGuidedCareClaimsStep2P7)}
                            </p>
                            <h3>{translator(messages.HealthStep3Header)}</h3>
                            <p>{translator(messages.HealthGuidedCareClaimsStep3P1)}</p>
                            <p>{translator(messages.HealthGuidedCareClaimsStep3P2)}</p>
                            <p>{translator(messages.HealthGuidedCareClaimsStep3P3)}</p>
                            <h3>{translator(messages.HealthStep4Header)}</h3>
                            <p>{translator(messages.HealthGuidedCareClaimsStep4P1)}</p>
                            <p>{translator(messages.HealthGuidedCareClaimsStep4P2)}</p>
                        </>
                    )
                },
                {
                    id: 'HealthCashBenefit',
                    header: translator(messages.HealthCashBenefitHeader),
                    content: (
                        <>
                            <p>{translator(messages.HealthCashBenefitP1)}</p>
                            <h3>{translator(messages.DentalClaimsHeader)}</h3>
                            <p>
                                {translator(messages.DentalClaimsP1, {
                                    link: (
                                        <Link
                                            href={CepConstants.CLAIM_PMI_BUPA_CLAIM_FORM_URL}
                                            target="_blank"
                                        >
                                            {translator(messages.DentalClaimsP1LinkText)}
                                        </Link>
                                    )
                                })}
                            </p>
                            <h3>{translator(messages.AllOtherClaimsHeader)}</h3>
                            <p>{translator(messages.AllOtherClaimsP1)}</p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.AllOtherClaimsP2)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.AllOtherClaimsP3)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.AllOtherClaimsP4)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.AllOtherClaimsP5)}
                            </p>
                            <p className="sgFaqTextBullet">
                                {translator(messages.AllOtherClaimsP6)}
                            </p>
                            <p>{translator(messages.AllOtherClaimsP7)}</p>
                            <p>{translator(messages.AllOtherClaimsP8)}</p>
                        </>
                    )
                }
            ],
            healthClaimsFaq: [
                {
                    id: 'claimFaq1',
                    header: translator(messages.HealthFaqQ1),
                    content: (
                        <>
                            <p>{translator(messages.HealthFaqA1)}</p>
                        </>
                    )
                },
                {
                    id: 'claimFaq2',
                    header: translator(messages.HealthFaqQ2),
                    content: (
                        <>
                            <p>{translator(messages.HealthFaqA2)}</p>
                        </>
                    )
                },
                {
                    id: 'claimFaq3',
                    header: translator(messages.HealthFaqQ3),
                    content: (
                        <>
                            <p>
                                {htmlParser(translator(messages.HealthFaqA3P1))}
                                {PolicyBookLink}
                                {htmlParser(translator(messages.HealthFaqA3P2))}
                            </p>
                        </>
                    )
                },
                {
                    id: 'claimFaq4',
                    header: translator(messages.HealthFaqQ4),
                    content: (
                        <>
                            <p>
                                {htmlParser(translator(messages.HealthFaqQ4A1))}
                            </p>
                            <p className="sgFaqTextBullet">
                                {htmlParser(translator(messages.HealthFaqQ4A2))}
                            </p>
                            <p className="sgFaqTextBullet">
                                {htmlParser(translator(messages.HealthFaqQ4A3))}
                            </p>
                            <p>{translator(messages.HealthFaqQ4A4)}</p>
                            <ul className="cepMotorClaimsStepsList">
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P1))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P2))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P3))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P4))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P5))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P6))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P7))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P8))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P9))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P10))}
                                </li>
                                <li>
                                    {(translator(messages.HealthFaqQ4A4P11))}
                                </li>
                            </ul>
                        </>
                    )
                },
                {
                    id: 'claimFaq5',
                    header: translator(messages.HealthFaqQ5),
                    content: (
                        <>
                            <p>
                                {translator(messages.HealthFaqA5P1, {
                                    link1: (
                                        <Link
                                            href={CepConstants.CLAIM_PMI_BUPA_LINK_CLAIM}
                                            target="_blank"
                                        >
                                            {translator(messages.HealthFaqA5Link1)}
                                        </Link>
                                    ),
                                    link2: (
                                        <Link
                                            href={CepConstants.CLAIM_PMI_BUPA_LINK_APP}
                                            target="_blank"
                                        >
                                            {translator(messages.HealthFaqA5Link2)}
                                        </Link>
                                    )
                                })}
                            </p>
                        </>
                    )
                },
                {
                    id: 'claimFaq6',
                    header: translator(messages.HealthFaqQ6),
                    content: (
                        <>
                            <p>
                                {htmlParser(translator(messages.HealthFaqA6P1))}
                                {PolicyBookLink}
                                {htmlParser(translator(messages.HealthFaqA6P2))}
                            </p>
                        </>
                    )
                }
            ]
        };
    }, [translator, isMobile, claimsPmiNumber]);

    const allAccordions = useMemo(() => {
        return _.concat(
            motorAccordions,
            homeAccordions.general,
            homeAccordions.insideHome,
            homeAccordions.outsideHome,
            healthAccordions.general,
            healthAccordions.healthClaimsFaq
        );
    }, [motorAccordions, homeAccordions, healthAccordions]);

    const accordionHeader = useCallback((isOpen, id) => {
        const { header } = _.find(allAccordions, { id: id });
        return (
            <div id={`accordionHeader${id}`}>
                <h4 id={`accordionHeaderHeader${id}`}>{header}</h4>
                {isOpen && (
                    <span id="chevronOpened">
                        <i className="mir mi-expand-less" />
                    </span>
                )}
                {!isOpen && (
                    <span id="chevronClosed">
                        <i className="mir mi-expand-more" />
                    </span>
                )}
            </div>
        );
    }, [allAccordions, translator]);

    const getAccordionCards = useCallback((accordions) => {
        return accordions.map((accordion) => {
            return (
                <AccordionCard
                    key={`claimsAccordion${accordion.id}`}
                    id={accordion.id}
                    renderHeader={accordionHeader}
                >
                    <div id={`accordionContent${accordion.id}`}>{accordion.content}</div>
                </AccordionCard>
            );
        });
    }, [accordionHeader]);

    const getMotorClaimsBaseInfo = useMemo(() => {
        return (
            <>
                <h2>{translator(messages.MotorClaims)}</h2>
                <p>
                    {translator(messages.MotorClaimsBaseInfoP1, {
                        phoneNumber: isMobile
                            ? <a href={`tel:${claimsAssistanceNumber.replaceAll(' ', '')}`}>{claimsAssistanceNumber}</a>
                            : <b>{claimsAssistanceNumber}</b>
                    })}
                </p>
                <p>
                    {translator(messages.MotorClaimsBaseInfoP2, {
                        phoneNumber1: isMobile
                            ? <a href={`tel:${claimsGlassNumber.replaceAll(' ', '')}`}>{claimsGlassNumber}</a>
                            : <b>{claimsGlassNumber}</b>,
                        phoneNumber2: isMobile
                            ? <a href={`tel:${claimsStandardNumber.replaceAll(' ', '')}`}>{claimsStandardNumber}</a>
                            : <b>{claimsStandardNumber}</b>
                    })}
                </p>
            </>
        );
    }, [translator, isMobile, claimsAssistanceNumber, claimsGlassNumber, claimsStandardNumber]);

    const getHomeClaimsBaseInfo = useMemo(() => {
        return (
            <>
                <h2>{translator(messages.HomeClaims)}</h2>
                <p>{htmlParser(translator(messages.HomeClaimsBaseInfoP1))}</p>
            </>
        );
    }, [translator]);

    const getHealthClaimsBaseInfo = useMemo(() => {
        return (
            <>
                <h2>{translator(messages.HealthClaims)}</h2>
                <p>{htmlParser(translator(messages.HealthClaimsBaseInfoP1))}</p>
            </>
        );
    }, [translator]);

    const getTravelClaimsBaseInfo = useMemo(() => {
        return (
            <>
                <h2>{translator(messages.TravelClaims)}</h2>
                <p>{htmlParser(translator(messages.TravelClaimsBaseInfoP1))}</p>
            </>
        );
    }, [translator]);

    const handleMotorClaimsReadMore = useCallback(() => {
        setMotorReadMoreOpened(true);
        setHomeReadMoreOpened(false);
        setHealthReadMoreOpened(false);
        setTravelReadMoreOpened(false);
        SgScrollUtils.scrollToElementById('motorClaimsSection');
    }, []);

    const handleHomeClaimsReadMore = useCallback(() => {
        setMotorReadMoreOpened(false);
        setHomeReadMoreOpened(true);
        setHealthReadMoreOpened(false);
        setTravelReadMoreOpened(false);
        SgScrollUtils.scrollToElementById('homeClaimsSection');
    }, []);

    const handleHealthClaimsReadMore = useCallback(() => {
        setMotorReadMoreOpened(false);
        setHomeReadMoreOpened(false);
        setHealthReadMoreOpened(true);
        setTravelReadMoreOpened(false);
        SgScrollUtils.scrollToElementById('healthClaimsSection');
    }, []);

    const handleTravelClaimsReadMore = useCallback(() => {
        setMotorReadMoreOpened(false);
        setHomeReadMoreOpened(false);
        setHealthReadMoreOpened(false);
        setTravelReadMoreOpened(true);
        SgScrollUtils.scrollToElementById('travelClaimsSection');
    }, []);

    const getMotorClaimsReadMoreSection = useMemo(() => {
        return (
            <>
                <p>{translator(messages.MotorClaimsBaseInfoP3)}</p>
                <h3>{translator(messages.MotorClaimsNextSteps)}</h3>
                <p>{translator(messages.MotorClaimsNextStepsP1)}</p>
                <ul className="cepMotorClaimsStepsList">
                    <li>{htmlParser(translator(messages.MotorNextStepsBullet1))}</li>
                    <li>{htmlParser(translator(messages.MotorNextStepsBullet2))}</li>
                    <li>{htmlParser(translator(messages.MotorNextStepsBullet3))}</li>
                    <li>{htmlParser(translator(messages.MotorNextStepsBullet4))}</li>
                    <li>{htmlParser(translator(messages.MotorNextStepsBullet5))}</li>
                </ul>
                <h3>{translator(messages.MotorContactUs)}</h3>
                <p>
                    {translator(messages.MotorContactUsP1, {
                        phoneNumber: isMobile
                            ? <a href={`tel:${claimsAssistanceNumber.replaceAll(' ', '')}`}>{claimsAssistanceNumber}</a>
                            : <b>{claimsAssistanceNumber}</b>
                    })}
                </p>
                <p>
                    {translator(messages.MotorContactUsP2, {
                        phoneNumber: isMobile
                            ? <a href={`tel:${claimsGlassNumber.replaceAll(' ', '')}`}>{claimsGlassNumber}</a>
                            : <b>{claimsGlassNumber}</b>
                    })}
                </p>
                <p>{translator(messages.MotorContactUsP3)}</p>
                <Accordion>
                    {getAccordionCards(motorAccordions)}
                </Accordion>
            </>
        );
    }, [getAccordionCards, motorAccordions, translator, isMobile, claimsAssistanceNumber, claimsGlassNumber]);

    const getHomeClaimsReadMoreSection = useMemo(() => {
        return (
            <>
                <h3>{translator(messages.HomeStep1Header)}</h3>
                <p>{translator(messages.HomeStep1P1)}</p>
                <p>{translator(messages.HomeStep1P2)}</p>
                <p>{htmlParser(translator(messages.HomeStep1P3))}</p>
                <h3>{translator(messages.HomeStep2Header)}</h3>
                <p>{translator(messages.HomeStep2P1)}</p>
                <p className="sgFaqTextBullet">
                    {translator(messages.HomeStep2P2)}
                </p>
                <p className="sgFaqTextBullet">
                    {translator(messages.HomeStep2P3)}
                </p>
                <h3>{translator(messages.HomeStep3Header)}</h3>
                <p>{translator(messages.HomeStep3P1)}</p>
                <p>{translator(messages.HomeStep3P2)}</p>
                <p>{translator(messages.HomeStep3P3)}</p>
                <p className="sgFaqTextBullet">
                    {translator(messages.HomeStep3P4)}
                </p>
                <p className="sgFaqTextBullet">
                    {translator(messages.HomeStep3P5)}
                </p>
                <p className="sgFaqTextBullet">
                    {translator(messages.HomeStep3P6)}
                </p>
                <p>{translator(messages.HomeStep3P7)}</p>
                <Accordion>
                    <h3>{translator(messages.AdditionalInformation)}</h3>
                    {getAccordionCards(homeAccordions.general)}
                    <div className="cepAccordionSpacer">&nbsp;</div>
                    <h3>{translator(messages.HelpPrevent)}</h3>
                    <h4>{translator(messages.InsideHome)}</h4>
                    {getAccordionCards(homeAccordions.insideHome)}
                    <div className="cepAccordionSpacer">&nbsp;</div>
                    <h4>{translator(messages.OutsideHome)}</h4>
                    <p>{translator(messages.VisualCheck)}</p>
                    {getAccordionCards(homeAccordions.outsideHome)}
                </Accordion>
            </>
        );
    }, [getAccordionCards, homeAccordions, translator]);

    const getHealthClaimsReadMoreSection = useMemo(() => {
        return (
            <>
                <Accordion>
                    {getAccordionCards(healthAccordions.general)}
                </Accordion>

                <div className="cepAccordionSpacer">&nbsp;</div>
                <h3>{translator(messages.MSKDirectHeader)}</h3>
                <p>{translator(messages.MSKDirectP1)}</p>
                <p>
                    {translator(messages.MSKDirectP2, {
                        phoneNumber: isMobile
                            ? <a href={`tel:${claimsPmiNumber.replaceAll(' ', '')}`}>{claimsPmiNumber}</a>
                            : <b>{claimsPmiNumber}</b>
                    })}
                </p>

                <h3>{translator(messages.AnytimeHealthLineHeader)}</h3>
                <p>{translator(messages.AnytimeHealthLineP1)}</p>
                <p>{translator(messages.AnytimeHealthLineP2)}</p>
                <p>{translator(messages.AnytimeHealthLineP3)}</p>
                <p>
                    {translator(messages.AnytimeHealthLineP4, {
                        phoneNumber: isMobile
                            ? <a href={`tel:${pmiNumber1.replaceAll(' ', '')}`}>{pmiNumber1}</a>
                            : <b>{pmiNumber1}</b>
                    })}
                </p>
                <p>{translator(messages.AnytimeHealthLineP5)}</p>

                <h3>{translator(messages.HealthFaqHeader)}</h3>
                <p>{translator(messages.HealthFaqP1)}</p>

                <Accordion>
                    {getAccordionCards(healthAccordions.healthClaimsFaq)}
                </Accordion>
            </>
        );
    }, [translator, healthAccordions, isMobile, pmiNumber1]);

    const getPhoneMessage = useCallback((firstPhoneNo, secondPhoneNo) => {
        return (
            <span>
                <span>{translator(messages.TravelClaimOnline)}</span>
                {translator(messages.TravelOrCall)}
                {!isMobile ? firstPhoneNo.replace('+44', '(+44)')
                    : <Link className="sgPhoneNumber" href={`tel:${firstPhoneNo.replace(/ /g, '')}`}>{ firstPhoneNo }</Link>}
                {translator(messages.TravelOr)}
                {!isMobile ? secondPhoneNo
                    : <Link className="sgPhoneNumber" href={`tel:${secondPhoneNo.replace(/ /g, '')}`}>{ secondPhoneNo }</Link>}
            </span>
        );
    }, [isMobile]);

    const travelClaimOnline = useCallback(() => {
        const url = CommonConstants.SAGA_CLAIMS_LINK;
        window.open(url, '_blank');
    }, []);

    const getTravelClaimsReadMoreSection = useMemo(() => {
        return (
            <>
                <p>
                    {translator(messages.TravelSpecialistTeam)}
                </p>
                <dl className="sgCepClaimTravelTelephoneNumbersList">
                    <dt>{translator(messages.TravelMedicalHelp)}</dt>
                    <dd>
                        {getPhoneMessage(SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_TYPE_MED_OR_PASSPORT_ABROAD,
                            SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_TYPE_MED_OR_PASSPORT)}
                    </dd>
                    <dt>{translator(messages.TravelLostOrStolenPassportHelp)}</dt>
                    <dd>
                        {getPhoneMessage(SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_TYPE_MED_OR_PASSPORT_ABROAD,
                            SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_TYPE_MED_OR_PASSPORT)}
                    </dd>
                    <dt>{translator(messages.TravelLostOrStolenBelongingsHelp)}</dt>
                    <dd>
                        {getPhoneMessage(SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_TYPE_BELONGINGS_CANX_ABROAD,
                            SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_TYPE_BELONGINGS_CANX)}
                    </dd>
                    <dt>{translator(messages.TravelSelectClaimsHelp)}</dt>
                    <dd>
                        {getPhoneMessage(SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_HELP_ABROAD,
                            SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_HELP)}
                    </dd>
                    <dt>{translator(messages.TravelCancellationHelp)}</dt>
                    <dd>
                        {getPhoneMessage(SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_TYPE_BELONGINGS_CANX_ABROAD,
                            SgSptConstants.TRAVEL_CLAIM_PHONE_NUMBERS.PHONE_NUMBER_CLAIM_TYPE_BELONGINGS_CANX)}
                    </dd>
                </dl>
                <Button className="sgButton sgPrimaryButton" onClick={travelClaimOnline}>
                    {translator(messages.TravelClaimOnline)}
                </Button>
            </>
        );
    }, [translator]);

    const overrideProps = {
        motorClaimsBaseInfo: {
            content: getMotorClaimsBaseInfo
        },
        motorClaimsReadMoreButton: {
            visible: !motorReadMoreOpened,
            onClick: () => handleMotorClaimsReadMore()
        },
        motorClaimsReadMoreSection: {
            visible: motorReadMoreOpened,
            content: getMotorClaimsReadMoreSection
        },
        homeClaimsBaseInfo: {
            content: getHomeClaimsBaseInfo
        },
        homeClaimsReadMoreButton: {
            visible: !homeReadMoreOpened,
            onClick: () => handleHomeClaimsReadMore()
        },
        homeClaimsReadMoreSection: {
            visible: homeReadMoreOpened,
            content: getHomeClaimsReadMoreSection
        },
        healthClaimsBaseInfo: {
            content: getHealthClaimsBaseInfo
        },
        healthClaimsReadMoreButton: {
            visible: !healthReadMoreOpened,
            onClick: () => handleHealthClaimsReadMore()
        },
        healthClaimsReadMoreSection: {
            visible: healthReadMoreOpened,
            content: getHealthClaimsReadMoreSection
        },
        travelClaimsBaseInfo: {
            content: getTravelClaimsBaseInfo
        },
        travelClaimsReadMoreButton: {
            visible: !travelReadMoreOpened,
            onClick: () => handleTravelClaimsReadMore()
        },
        travelClaimsReadMoreSection: {
            visible: travelReadMoreOpened,
            content: getTravelClaimsReadMoreSection
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {},
        resolveComponentMap: {
            SgCepSideMenu
        }
    };

    return renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers);
}

export default ClaimsPage;
